import type { MutationKey, QueryKey } from "@tanstack/react-query"

import { seconds } from "@/3514/utils"

enum EQueryKeyBase {
    CoachingChatConversation = "CoachingChatConversation",
    CoachingChatNewMessages = "CoachingChatNewMessages",
    CoachingChatDisclaimer = "CoachingChatDisclaimer"
}

enum EMutationKeyBase {
    Seed = "ChatSeed",
    Conversation = "ChatConversation",
    Message = "ChatMessage",
    DisclaimerAwareness = "ChatDisclaimerAwareness",
    MatrixFeedback = "ChatMatrixFeedback",
    SocketToken = "ChatSocketToken"
}

const EQueryKey: { Conversation(id: number): QueryKey; NewMessages(id: number): QueryKey; Disclaimer(): QueryKey } = {
    Conversation: (id: number): QueryKey => [EQueryKeyBase.CoachingChatConversation, id],
    NewMessages: (id: number): QueryKey => [EQueryKeyBase.CoachingChatNewMessages, id],
    Disclaimer: (): QueryKey => [EQueryKeyBase.CoachingChatDisclaimer]
} as const

const EQueryUrl: {
    GetConversation(conversationId: number): string
    GetConversationNewMessages(conversationId: number): string
    CoachingChatDisclaimer(): string
} = {
    GetConversation: (conversationId: number): string => `/api/practice-lab/conversation/${conversationId}/`,
    GetConversationNewMessages: (conversationId: number): string =>
        `/api/practice-lab/conversation/${conversationId}/new-messages/`,
    CoachingChatDisclaimer: (): string => "/api/ai-disclaimer/"
} as const

const EMutationKey: {
    Seed(chapterId: number): MutationKey
    CreateConversation(chapterId: number): MutationKey
    CreateMessage(): MutationKey
    CreateDisclaimerAwareness(): MutationKey
    CreateMatrixFeedback(): MutationKey
    CreateSocketToken(): MutationKey
} = {
    Seed: (chapterId: number): MutationKey => [EMutationKeyBase.Seed, chapterId],
    CreateConversation: (chapterId: number): MutationKey => [EMutationKeyBase.Conversation, chapterId],
    CreateMessage: (): MutationKey => [EMutationKeyBase.Message],
    CreateDisclaimerAwareness: (): MutationKey => [EMutationKeyBase.DisclaimerAwareness],
    CreateMatrixFeedback: (): MutationKey => [EMutationKeyBase.MatrixFeedback],
    CreateSocketToken: (): MutationKey => [EMutationKeyBase.SocketToken]
} as const

const EMutationUrl: {
    HandleSeed(): string
    CreateConversation(): string
    CreateMessage(conversationId: number): string
    CreateDisclaimerAwareness(): string
    CreateMatrixFeedback(conversationId: number): string
    CreateSocketToken(): string
} = {
    HandleSeed: (): string => "/api/practice-lab/conversation/seed-question-answer/",
    CreateConversation: (): string => "/api/practice-lab/conversation/start-chat/",
    CreateMessage: (conversationId: number): string => `/api/practice-lab/conversation/${conversationId}/message/`,
    CreateDisclaimerAwareness: (): string => "/api/ai-disclaimer/",
    CreateMatrixFeedback: (conversationId: number): string =>
        `/api/practice-lab/conversation/${conversationId}/matrix-feedback/`,
    CreateSocketToken: (): string => "/api/realtime/ws-token/"
}

const NEW_MESSAGES_QUERY_SHORT_POLLING_REFETCH_INTERVAL_IN_MS: number = seconds(2)
const NEW_MESSAGES_QUERY_LONG_POLLING_REFETCH_INTERVAL_IN_MS: number = seconds(15)

const timers = {
    NEW_MESSAGES_QUERY_SHORT_POLLING_REFETCH_INTERVAL_IN_MS,
    NEW_MESSAGES_QUERY_LONG_POLLING_REFETCH_INTERVAL_IN_MS
}

export {
    EQueryKeyBase as ECoachingChatQueryKeyBase,
    EMutationKeyBase as ECoachingChatMutationKeyBase,
    EQueryKey as ECoachingChatQueryKey,
    EMutationKey as ECoachingChatMutationKey,
    EQueryUrl as ECoachingChatApiQueryUrl,
    EMutationUrl as ECoachingChatMutationUrl,
    timers as coachingChatApiTimers
}
